/**
 * Zod schema + defaults for the Add-property wizard. The form model is the flat
 * `AddPropertyForm` shape consumed by `@/lib/add-property`; every numeric field
 * is a `number` (fed by `NumberInput`, which always emits numbers).
 */
import { z } from "zod";

const DATE_OR_EMPTY = z.union([
  z.literal(""),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD"),
]);

const baseSchema = z.object({
  // Step 1 — property
  addr1: z.string().trim().min(1, "Street address is required"),
  city: z.string().trim().default(""),
  state: z.string().trim().default(""),
  zip: z.string().trim().default(""),
  bd: z.number().min(0),
  ba: z.number().min(0),
  sqft: z.number().min(0),
  strategy: z.enum(["Rental", "BRRRR", "Flip"]),
  status: z.enum(["owned", "in-rehab"]),
  purchaseDate: DATE_OR_EMPTY,
  imgUrl: z.union([z.literal(""), z.string().url("Enter a valid image URL")]),
  // Step 2 — financing
  purchase: z.number().min(1, "Enter a purchase price"),
  loanValue: z.number().min(0),
  rate: z.number().min(0),
  termMonths: z.number().min(0),
  termUnit: z.enum(["months", "years"]),
  points: z.number().min(0),
  emiDate: DATE_OR_EMPTY,
  brrrrLoanKind: z.enum(["hard_money", "long_term"]),
  arv: z.number().min(1, "Enter an estimated after-repair value"),
  // Step 3 — income
  monthlyRent: z.number().min(0),
  rehabCost: z.number().min(0),
  sellPct: z.number().min(0).max(100),
});

/**
 * Flip must carry a selling-cost percent — it drives `flip_selling_cost` and the
 * profit/margin tiles, which read as a loss without it. Other strategies never
 * show the field, so their (unused) default passes through untouched.
 */
export const addPropertySchema = baseSchema.superRefine((values, ctx) => {
  if (values.strategy === "Flip" && values.sellPct <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sellPct"],
      message: "Enter an estimated selling cost",
    });
  }
});

export type AddPropertyFormValues = z.infer<typeof baseSchema>;

/** Compile-time defaults — Rental long-term terms; owned status. */
export const ADD_PROPERTY_DEFAULTS: AddPropertyFormValues = {
  addr1: "",
  city: "",
  state: "",
  zip: "",
  bd: 0,
  ba: 0,
  sqft: 0,
  strategy: "Rental",
  status: "owned",
  purchaseDate: "",
  imgUrl: "",
  purchase: 0,
  loanValue: 0,
  rate: 7,
  termMonths: 360,
  termUnit: "years",
  points: 1,
  emiDate: "",
  brrrrLoanKind: "hard_money",
  arv: 0,
  monthlyRent: 0,
  rehabCost: 0,
  // App-wide default selling cost (matches `DEFAULT_SELL_PCT`, the fallback in
  // `resolveSellingCost`).
  sellPct: 7,
};

/** Field names validated at each wizard step (drives per-step `form.trigger`). */
export const STEP_FIELDS: ReadonlyArray<ReadonlyArray<keyof AddPropertyFormValues>> = [
  ["addr1", "city", "state", "zip", "bd", "ba", "sqft", "strategy", "status", "purchaseDate", "imgUrl"],
  ["purchase", "loanValue", "rate", "termMonths", "termUnit", "points", "emiDate", "brrrrLoanKind", "arv"],
  ["monthlyRent", "rehabCost", "sellPct"],
];

export const STEP_NAMES = ["Property", "Financing", "Income"] as const;
